import BlogCategoryMenu from '@/components/blog/BlogCategoryMenu'
import CategoryPageHeader from '@/components/blog/CategoryPageHeader'
import CategoryPagePostsList from '@/components/blog/CategoryPagePostsList'
import PageSeo from '@/components/core/PageSeo'
import BlogCategoryLd from '@/components/jsonLd/BlogCategoryLd'
import Footer from '@/components/layout/Footer'
import MainMenu from '@/components/layout/MainMenu'
import { REVALIDATE_TIME } from '@/env'
import type {
  BlogCategoryEntity,
  BlogCategoryEntityResponse,
  BlogMenuEntity,
  ComponentSharedSeo,
  FooterEntity,
  PromoEntity,
} from '@/gql/generated/graphql'
import { Api } from '@/services/api'
import type { GetStaticPaths, GetStaticProps } from 'next'
import React, { Fragment } from 'react'

type Props = {
  menu: BlogMenuEntity
  footer: FooterEntity
  category: BlogCategoryEntityResponse
  categories: BlogCategoryEntity[]
  promos: PromoEntity[]
  isWhiteTheme?: boolean
  hideLocales?: boolean
  notSticky?: boolean
}

export default function SingleCategoryPage(props: Props) {
  const slug = `blog/category/${props.category.data?.attributes?.slug ?? ''}`
  const title = `Blog | ${props.category.data?.attributes?.title}`

  return (
    <Fragment>
      <PageSeo
        data={props.category.data?.attributes?.seo as ComponentSharedSeo}
        slug={slug}
        title={title}
      />
      <MainMenu
        promos={props.promos}
        notSticky={props.notSticky}
        isWhiteTheme={true}
        hideLocales={true}
        menu={props.menu}
      />
      <BlogCategoryMenu categories={props.categories} />
      <CategoryPageHeader category={props.category} />
      <BlogCategoryLd category={props.category} />
      <CategoryPagePostsList category={props.category} />
      <Footer menu={props.footer} />
    </Fragment>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const slug = params?.categorySlug as string
  try {
    const [category, menu, footer, categories, promos] = await Promise.all([
      Api.SingleCategory(slug, { pageSize: 25 }),
      Api.BlogMenu(locale),
      Api.FooterMenu(),
      Api.BlogCategories({ pageSize: 10 }),
      Api.PromoBanners(),
    ])

    if (category.data === null) return { notFound: true }

    return {
      props: { category, menu, footer, categories, promos },
      revalidate: REVALIDATE_TIME,
    }
  } catch (error) {
    return {
      notFound: true,
    }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { categorySlug: string } }[] = []
  const categories = (await Api.BlogPostSlugs()) ?? []
  for (const category of categories) {
    if (category.attributes?.slug) {
      paths.push({ params: { categorySlug: category.attributes?.slug } })
    }
  }

  return {
    paths,
    fallback: 'blocking',
  }
}
